import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";

const HEADLINE = ["Engineering the Future", "with Intelligent", "Technology"];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const line = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

const FLOATERS = [
  { label: "AWS", top: "16%", left: "70%", d: 0 },
  { label: "GCP", top: "40%", left: "50%", d: 0 },
  { label: "Kubernetes", top: "84%", left: "65%", d: 1.2 },
  { label: "Azure", top: "24%", left: "80%", d: 0.6 },
  { label: "LLM", top: "72%", left: "78%", d: 1.8 },
  { label: "Terraform", top: "48%", left: "85%", d: 2.2 },
];

export default function Hero() {
  return (
    <section className="relative min-h-[75vh] flex items-center overflow-hidden noise pt-16 pb-8">
      <div className="absolute inset-0 grid-bg opacity-70" />
      <div
        className="absolute inset-0 -z-0"
        style={{ background: "radial-gradient(ellipse 70% 55% at 50% -5%, rgba(0,85,255,0.22), transparent 70%)" }}
      />
      <div
        className="absolute -bottom-40 left-1/2 -translate-x-1/2 h-[560px] w-[560px] rounded-full blur-[130px] opacity-30"
        style={{ background: "conic-gradient(from 90deg, var(--brand), var(--secondary), var(--accent), var(--brand))" }}
      />

      {FLOATERS.map((f) => (
        <div
          key={f.label}
          className="hidden md:flex absolute animate-float items-center gap-2 rounded-full glass px-3.5 py-1.5 text-xs font-mono text-textsub"
          style={{ top: f.top, left: f.left, animationDelay: `${f.d}s` }}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-brand" /> {f.label}
        </div>
      ))}

      <div className="relative max-w-[90rem] mx-auto px-6 lg:px-12 w-full">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs font-mono uppercase tracking-[0.2em] text-brand mb-6"
          data-testid="hero-badge"
        >
          <Sparkles size={13} /> VynFive Techlabs LLP
        </motion.div>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          data-testid="hero-headline"
          className="font-heading font-bold tracking-tighter leading-[0.98] text-5xl sm:text-6xl md:text-7xl lg:text-[5.7rem] max-w-5xl"
        >
          {HEADLINE.map((l, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span variants={line} className="block">
                {i === 1 ? (
                  <>
                    with <span className="text-gradient">Intelligent</span>
                  </>
                ) : (
                  l
                )}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-6 max-w-2xl text-base md:text-lg text-textsub leading-relaxed"
        >
          Helping businesses accelerate innovation through Cloud Engineering, DevOps,
          AI, Software Development, and IT Talent Solutions.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-cta-primary"
            className="group inline-flex items-center gap-2 rounded-full bg-brand text-[#05050A] font-semibold px-7 py-3.5 glow-brand hover:gap-3 transition-all duration-300"
          >
            Book Consultation
            <ArrowUpRight size={17} className="transition-transform group-hover:rotate-45" />
          </a>
          <a
            href="#services"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-2 rounded-full glass px-7 py-3.5 font-semibold hover:border-brand transition-colors duration-300"
          >
            Explore Services
          </a>
        </motion.div>
      </div>
    </section>
  );
}
